/**
 * Espejo cliente de `App\Support\EmailMask` (fc-api).
 *
 * El backend nunca devuelve el correo completo en la recuperación de
 * contraseña: sólo la pista enmascarada. Para poder decirle al usuario "eso no
 * coincide con la pista que elegiste" ANTES de gastar el submit, replicamos el
 * mismo algoritmo acá y comparamos `mascara(loQueEscribió) === pistaElegida`.
 *
 * Esto NO revela nada: la máscara se calcula sobre un texto que el propio
 * usuario acaba de tipear. La verificación autoritativa la sigue haciendo el
 * API en `POST /auth/forgot-password` — acá sólo ganamos feedback inmediato.
 *
 * Si cambia `EmailMask::apply()` en la API, este archivo tiene que cambiar con
 * él o el formulario va a rechazar correos correctos.
 */

/** Enmascara igual que `EmailMask::apply()`: mismo dominio, mismo largo. */
export function maskEmail(email: string | null | undefined): string {
  const limpio = (email ?? '').trim()
  if (limpio === '' || !limpio.includes('@')) return '***'

  const corte = limpio.indexOf('@')
  const local = limpio.slice(0, corte)
  const dominio = limpio.slice(corte + 1)

  // `Array.from` y no `.length`: el largo tiene que contarse en puntos de
  // código, igual que el `mb_strlen` de PHP, o un correo con tilde produce
  // una máscara de largo distinto al de la API y nunca coincidiría.
  const chars = Array.from(local)
  const len = chars.length

  let enmascarado: string
  if (len <= 2) {
    enmascarado = chars.slice(0, 1).join('') + '*'
  } else if (len <= 4) {
    enmascarado = chars[0] + '*'.repeat(len - 2) + chars[len - 1]
  } else {
    enmascarado = chars.slice(0, 2).join('') + '*'.repeat(len - 3) + chars[len - 1]
  }

  return `${enmascarado}@${dominio}`
}

/**
 * ¿El correo tipeado corresponde a esa pista?
 *
 * Comparación insensible a mayúsculas: la API enmascara preservando el casing
 * con que el correo quedó guardado (`Rhi.Perez@Gmail.com` → `Rh.****z@Gmail.com`),
 * y nadie tipea su correo respetando ese casing.
 */
export function coincideConMascara(email: string, mascara: string): boolean {
  return maskEmail(email).toLowerCase() === mascara.trim().toLowerCase()
}
